// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Device-motion nudging (M3-34, 08 §5.2).
//
// > "Nudge — device: physically jerk the phone; the accelerometer supplies direction and
// > magnitude. Device-motion nudging requires an explicit permission prompt on iOS
// > (`DeviceMotionEvent.requestPermission`). It is offered once, framed as *"Nudge by moving your
// > phone"*, declines gracefully, and thumb-flick nudging always remains available."
//
// **This module is entirely optional and nothing depends on it.** That is the design, not a
// caveat: it is the one input in the product a browser is allowed to refuse outright, and the
// player who refuses it must end up with a machine that is complete rather than a machine with a
// missing control. Everything a device nudge does, a thumb flick already does — same queue, same
// units, same tilt consequences — so a decline costs a gesture, not a capability.
//
// Three problems, none of them the permission prompt:
//
// **Gravity is nine-point-eight metres per second squared and it never stops.** The reading a
// phone hands out is dominated by it, and a phone resting at an angle on a table reads as a
// permanent shove in that direction. `acceleration` is the linear part with gravity already
// removed — but it is `null` on any device with no gyroscope, which is most cheap Android
// hardware, so the fallback path has to estimate gravity itself and subtract it. A slow low-pass
// does that: gravity is the part of the signal that does not change, which is exactly what a
// long-time-constant average keeps.
//
// **A shove is not a sample, it is an event with a shape.** One shove crosses the threshold,
// rises to a peak and decays over perhaps a hundred and fifty milliseconds — twelve samples at
// the usual rate. Firing one nudge per sample over threshold turns a single shove into a tilt;
// firing one nudge on the first sample over threshold reads the *leading edge* of the shove and
// so consistently understates it, because the leading edge is by definition the weakest part.
// So a shove is tracked as a unit, and the impulse is delivered **incrementally** as it grows:
// the ball feels the shove build the way it would on a real cabinet, and the total delivered is
// the peak rather than the edge. It ends when the acceleration falls back through a lower
// release threshold, which a genuine second shove must pass through anyway — so a double nudge
// is still two nudges, and a ringing sensor is not.
//
// **The accelerometer is bolted to the device and the game is drawn on the screen.** They are
// the same frame only in the phone's natural orientation. Rotate the phone into landscape and a
// shove the player sees as "to the right" arrives on the device's *y* axis, so an unrotated
// mapping nudges the machine ninety degrees away from the direction the player shoved it —
// which does not read as a bug, it reads as a machine that does not work.

import { NUDGE_IMPULSE, type PlayControls } from './desktop'

/**
 * How hard a jerk has to be to count, in m/s² of linear acceleration.
 *
 * Walking with the phone in your hand peaks around 2–3 m/s², and a hand holding a phone still is
 * never quieter than about 0.3. Four is above both without being a shove nobody can produce
 * one-handed while also holding a flipper.
 */
export const MOTION_THRESHOLD = 4

/** …and this hard to be a full-strength one. */
export const MOTION_FULL = 14

/**
 * A shove is over when the acceleration falls back below this.
 *
 * Below the trigger threshold rather than at it, deliberately: a signal hovering on one
 * threshold would start and end a shove on alternate samples and deliver a burst of small
 * impulses. The gap is the hysteresis that makes one shove one event.
 */
export const MOTION_RELEASE = 2

/** After a shove ends, ignore this long. Long enough to kill sensor ring, short enough for a
 *  deliberate double shove — which no hand produces inside a tenth of a second. */
export const MOTION_REFRACTORY_MS = 120

/** The weakest and strongest a device nudge can be, as multiples of one key-press of nudge. */
export const MOTION_MIN_SCALE = 0.4
export const MOTION_MAX_SCALE = 1.5

/**
 * The gravity estimator's time constant, in seconds.
 *
 * Long, because gravity is a constant and a shove is not: anything this average tracks is
 * something the player is not doing. Too short and it learns the shove and cancels it; too long
 * and turning the phone over leaves a phantom shove for several seconds. Four hundred
 * milliseconds is about six times the length of a shove.
 */
export const GRAVITY_TAU_S = 0.4

/** What a `DeviceMotionEvent` gives us, so a test can send one without a phone. */
export interface MotionSample {
  readonly acceleration?: { x: number | null; y: number | null; z: number | null } | null
  readonly accelerationIncludingGravity?: {
    x: number | null
    y: number | null
    z: number | null
  } | null
  /** Milliseconds between readings, as the device reports it. */
  readonly interval?: number | null
}

/** iOS 13+ gates the sensor behind this. Everywhere else the constructor exists without it. */
export interface DeviceMotionConstructorLike {
  requestPermission?: () => Promise<string>
}

/** The window, reduced to the four things this reads. */
export interface MotionHost {
  addEventListener: (
    type: string,
    listener: (event: Event) => void,
    options?: boolean | AddEventListenerOptions,
  ) => void
  removeEventListener: (
    type: string,
    listener: (event: Event) => void,
    options?: boolean | EventListenerOptions,
  ) => void
  DeviceMotionEvent?: DeviceMotionConstructorLike
  /** Which way up the *content* is, in degrees. `screen.orientation.angle`, when there is one. */
  screenAngle?: () => number
}

/**
 * What the platform will let us do.
 *
 * `unsupported` — no sensor API at all: a desktop browser, or a locked-down one. Nothing is
 * offered, because an offer that cannot be accepted is a control that does not work.
 * `unasked` — iOS, and the player has not been through the prompt. `granted` / `denied` — they
 * have.
 */
export type MotionPermission = 'unsupported' | 'unasked' | 'granted' | 'denied'

/**
 * The window, if this browser has a motion sensor API at all.
 *
 * Assembled explicitly rather than handing `globalThis` straight over. Twice now a host
 * defaulted to `globalThis` and silently lost the half of its surface that lives on `document`
 * or on `screen`; `screenAngle` is exactly that shape of property, and a host missing it does
 * not fail — it quietly maps every landscape nudge ninety degrees out.
 */
export function browserMotionHost(): MotionHost | undefined {
  const global = globalThis as {
    addEventListener?: MotionHost['addEventListener']
    removeEventListener?: MotionHost['removeEventListener']
    DeviceMotionEvent?: DeviceMotionConstructorLike
    screen?: { orientation?: { angle?: number } }
  }
  if (global.addEventListener === undefined || global.removeEventListener === undefined) {
    return undefined
  }
  if (global.DeviceMotionEvent === undefined) return undefined
  return {
    addEventListener: (type, listener, options) =>
      global.addEventListener?.(type, listener, options),
    removeEventListener: (type, listener, options) =>
      global.removeEventListener?.(type, listener, options),
    DeviceMotionEvent: global.DeviceMotionEvent,
    // Read on every shove rather than captured once: `screen.orientation.angle` is the one
    // value here that changes while a machine is on screen, which is the whole reason it exists.
    screenAngle: () => {
      const angle = global.screen?.orientation?.angle
      return typeof angle === 'number' && Number.isFinite(angle) ? angle : 0
    },
  }
}

/** Does this browser have the sensor API, whatever it will later say about permission? */
export function motionSupported(host: MotionHost | undefined): boolean {
  return host !== undefined && host.DeviceMotionEvent !== undefined
}

/**
 * Is this a device the offer belongs on?
 *
 * Two questions, and the second is not about capability at all. Some laptops have an
 * accelerometer and would pass `motionSupported` — and "Nudge by moving your phone" on a machine
 * bolted to a desk is a control that reads as a bug in the copy. A coarse pointer is the
 * capability query for "this is held in a hand", the same one the tier ceiling uses, and it is
 * passed in rather than read here because the media query lives on the window and this module
 * has been careful all the way down not to assume one.
 */
export function shouldOfferMotionNudge(
  host: MotionHost | undefined,
  coarsePointer: boolean,
): boolean {
  return motionSupported(host) && coarsePointer
}

/**
 * Does accepting the offer put a *system* prompt in front of the player?
 *
 * The offer's wording depends on the answer. On iOS "Turn it on" is followed by Safari's own
 * dialogue, and a button that silently summons a second dialogue is a button that has lied about
 * what it does; everywhere else the offer *is* the consent and there is nothing after it.
 */
export function motionNeedsSystemPrompt(host: MotionHost | undefined): boolean {
  return typeof host?.DeviceMotionEvent?.requestPermission === 'function'
}

/**
 * Ask for the sensor. **Must be called from inside a user gesture** on iOS.
 *
 * A browser with no `requestPermission` grants by having nothing to ask, which is the honest
 * reading: on those platforms the sensor is simply readable and our own offer is the only
 * consent there is.
 *
 * A *throw* is not a decline. iOS throws `NotAllowedError` when the call did not come from a
 * gesture — a mistake in our own code, not a decision by the player — so it answers `unasked`
 * and leaves the offer standing. Recording it as a decline would disable the feature
 * permanently on the strength of our own bug.
 */
export async function requestMotionPermission(
  host: MotionHost | undefined,
): Promise<MotionPermission> {
  if (!motionSupported(host)) return 'unsupported'
  const ask = host?.DeviceMotionEvent?.requestPermission
  if (typeof ask !== 'function') return 'granted'
  try {
    const answer = await ask.call(host?.DeviceMotionEvent)
    if (answer === 'granted') return 'granted'
    if (answer === 'denied') return 'denied'
    return 'unasked'
  } catch {
    return 'unasked'
  }
}

export interface MotionNudgeOptions {
  /** The same object the keyboard and the touch layer mutate. */
  readonly controls: PlayControls
  /** Should a nudge be delivered at all right now? False while paused, or between games. */
  readonly enabled?: () => boolean
  /** Told each time a shove starts, so the machine can show it was felt. */
  readonly onShove?: (magnitude: number) => void
}

interface Shove {
  /** How much impulse this shove has already put into the machine. */
  delivered: number
}

/**
 * Listen for shoves and turn them into nudges.
 *
 * Returns the detach, which the caller owes: a `devicemotion` listener left on the window keeps
 * a whole machine alive and keeps nudging one nobody is looking at.
 */
export function attachMotionNudge(
  host: MotionHost,
  options: MotionNudgeOptions,
): { detach: () => void } {
  const { controls } = options
  // Estimated gravity, in device axes. Undefined until the first sample seeds it — starting at
  // zero would make the first reading look like a 9.8 m/s² shove straight down.
  let gravity: { x: number; y: number; z: number } | undefined
  let shove: Shove | undefined
  /** Milliseconds of quiet still owed before another shove may start. */
  let refractory = 0

  const onMotion = (event: Event): void => {
    const sample = event as unknown as MotionSample
    const dt = intervalSeconds(sample)

    const linear = linearAcceleration(sample, gravity)
    if (linear === undefined) return
    gravity = linear.gravity

    if (refractory > 0) {
      refractory = Math.max(0, refractory - dt * 1000)
      // Still ringing. The gravity estimate above keeps running, deliberately — it must not lose
      // four hundred milliseconds of tracking every time the player nudges.
      return
    }

    // Device axes into screen axes. Both have +y running up the screen, so this is a rotation
    // and nothing else — but it is a rotation that only matters once the phone is turned, which
    // is why it is a named function with its own test rather than three lines here.
    const angle = host.screenAngle?.() ?? 0
    const [sx, sy] = toScreen(linear.x, linear.y, angle)
    const magnitude = Math.sqrt(sx * sx + sy * sy)

    if (shove === undefined) {
      if (magnitude < MOTION_THRESHOLD) return
      if (options.enabled !== undefined && !options.enabled()) return
      shove = { delivered: 0 }
      options.onShove?.(magnitude)
    } else if (magnitude < MOTION_RELEASE) {
      shove = undefined
      refractory = MOTION_REFRACTORY_MS
      return
    }

    // The impulse this shove has earned so far. Delivered as the difference, so a shove that
    // keeps growing keeps pushing and a shove that has peaked stops — one shove, one total.
    const wanted = NUDGE_IMPULSE * motionScale(magnitude)
    const owed = wanted - shove.delivered
    if (owed <= 0) return
    shove.delivered = wanted
    // The playfield's +y runs up the table and the screen's +y — after `toScreen` — runs up the
    // screen, which is the same direction. A shove towards the top of the phone shoves the
    // machine up the playfield, as it does on a cabinet.
    controls.nudges.push([(sx / magnitude) * owed, (sy / magnitude) * owed])
  }

  host.addEventListener('devicemotion', onMotion)
  return {
    detach: () => {
      host.removeEventListener('devicemotion', onMotion)
    },
  }
}

/** How strong a nudge an acceleration of this magnitude is, as a multiple of one key-press. */
export function motionScale(magnitude: number): number {
  const span = MOTION_FULL - MOTION_THRESHOLD
  const t = span <= 0 ? 1 : (magnitude - MOTION_THRESHOLD) / span
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t
  return MOTION_MIN_SCALE + (MOTION_MAX_SCALE - MOTION_MIN_SCALE) * clamped
}

/**
 * Rotate a device-frame vector into the screen frame, +y up.
 *
 * `screen.orientation.angle` is how far the *content* has been rotated away from the device's
 * natural orientation, so undoing it is a rotation by `-angle` — the same correction every
 * device-orientation control applies, and the reason it is a named function with its own test
 * is that getting the sign wrong produces a machine that nudges the wrong way only in landscape.
 */
export function toScreen(x: number, y: number, angleDegrees: number): [number, number] {
  const quarter = ((Math.round(angleDegrees / 90) % 4) + 4) % 4
  switch (quarter) {
    case 1:
      return [y, -x]
    case 2:
      return [-x, -y]
    case 3:
      return [-y, x]
    default:
      return [x, y]
  }
}

/**
 * The largest `interval` that cannot possibly be milliseconds.
 *
 * A sensor reporting one-millisecond samples would be running at a thousand hertz, which no phone
 * does and no browser exposes; the fastest real rate is about a hundred. So a value at or below
 * this is not a very fast sensor, it is a device quoting seconds — and one second between samples
 * is itself well past the backgrounded ceiling below, so nothing legitimate lands here either way.
 */
const INTERVAL_SECONDS_MAX = 1

/**
 * Seconds since the previous sample, from what the device says, with a sane floor and ceiling.
 *
 * **The units are not reliable, and getting them wrong stops nudging dead.** The specification
 * says milliseconds and Chrome obliges; iOS Safari has long reported *seconds* — `0.016` where
 * Android sends `16`. Read naively that is a thousandfold under-count, and the damage lands on
 * the refractory drain in `attachMotionNudge`, which subtracts `dt * 1000` from a 120 ms budget:
 * at 0.016 ms a sample it needs some seven thousand samples, about two minutes, to clear. Every
 * sample in that window returns early, before the threshold test, so the first shove of a session
 * works and every shove after it is discarded without being looked at — which does not read as a
 * unit bug, it reads as a phone whose sensor died after one nudge.
 *
 * Exported and separately tested for the same reason `toScreen` is: it is a small pure function
 * whose failure is invisible at the call site and expensive on real hardware.
 */
export function intervalSeconds(sample: MotionSample): number {
  const reported = sample.interval
  if (typeof reported !== 'number' || !Number.isFinite(reported) || reported <= 0) return 1 / 60
  // Believe the magnitude rather than the platform: sniffing for iOS would miss the next browser
  // to do this, and every value this rescales is one that is meaningless as milliseconds anyway.
  const ms = reported <= INTERVAL_SECONDS_MAX ? reported * 1000 : reported
  // A device that reports a whole second between samples has been backgrounded; treating that
  // as real would make the gravity estimator jump straight to the new reading.
  return Math.min(ms, 100) / 1000
}

/**
 * The linear acceleration in device axes, and the gravity estimate to carry forward.
 *
 * Two sources, and the good one is not always there. `acceleration` is the sensor fusion's own
 * gravity-free reading and needs no help; it is `null` on hardware with no gyroscope, where all
 * that exists is the raw reading with gravity in it and the subtraction is ours to do.
 */
function linearAcceleration(
  sample: MotionSample,
  gravity: { x: number; y: number; z: number } | undefined,
):
  | { x: number; y: number; gravity: { x: number; y: number; z: number } | undefined }
  | undefined {
  const linear = sample.acceleration
  if (linear !== null && linear !== undefined && typeof linear.x === 'number' && typeof linear.y === 'number') {
    return { x: linear.x, y: linear.y, gravity }
  }

  const raw = sample.accelerationIncludingGravity
  if (raw === null || raw === undefined) return undefined
  const x = raw.x ?? 0
  const y = raw.y ?? 0
  const z = raw.z ?? 0
  if (gravity === undefined) {
    // The first reading is almost all gravity, so it is the best possible seed — and seeding
    // with it means the first sample produces no nudge, which is right: the player has not
    // moved yet.
    return { x: 0, y: 0, gravity: { x, y, z } }
  }
  const dt = intervalSeconds(sample)
  const alpha = dt / (GRAVITY_TAU_S + dt)
  const next = {
    x: gravity.x + (x - gravity.x) * alpha,
    y: gravity.y + (y - gravity.y) * alpha,
    z: gravity.z + (z - gravity.z) * alpha,
  }
  return { x: x - next.x, y: y - next.y, gravity: next }
}
